import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  createFocusHandle,
  createOnBlur,
  FilterInputProps,
} from '../../common/tanstacktable';
import {
  DATE_FORMAT,
  FormattedDate,
  getDayjs,
  getIsoDateFromFormattedDate,
} from '../../common/utils';
import { DatePicker, FocusRef, TextEllipsis } from '../../common/components';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { Checkbox, Flex } from 'antd';
import translations from './ValueDateSelectFilterInput.translations.json';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

const { addTranslations, t } = i18nNsFactory('value-date-select-filter-input');
addTranslations(translations);

export interface ValueDataSelectFilterValueType {
  onlyLastValueDate?: boolean;
  valueDate?: [FormattedDate | null, FormattedDate | null];
}

export const ValueDateSelectFilterInput = forwardRef<
  FocusRef,
  FilterInputProps
>(({ header, onBlur }, ref) => {
  const id = `${header.column.id}-filter-input`;

  useImperativeHandle(ref, () => createFocusHandle(`${id}-checkbox`));

  const handleOnBlur = useMemo(
    () => createOnBlur([id, `${id}-popup`, `${id}-checkbox`], onBlur),
    [id, onBlur],
  );

  const filterValue = header.column.getFilterValue() as
    | ValueDataSelectFilterValueType
    | undefined;

  const onlyLastValueDate =
    (filterValue?.onlyLastValueDate as boolean) ?? false;
  const valueDateRange = useMemo(
    () =>
      filterValue?.valueDate?.map(
        (v) => v && getDayjs(getIsoDateFromFormattedDate(v))!,
      ) as [Dayjs | null, Dayjs | null],
    [filterValue],
  );

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent) => {
      header.column.setFilterValue(() =>
        event.target.checked
          ? {
              valueDate: undefined,
              onlyLastValueDate: true,
            }
          : undefined,
      );
    },
    [header.column],
  );

  const handleDatePickerChange = useCallback(
    (_dates: any, valueDate: [string, string]) => {
      header.column.setFilterValue(() =>
        valueDate?.some(Boolean)
          ? {
              onlyLastValueDate: false,
              valueDate,
            }
          : undefined,
      );
    },
    [header.column],
  );

  return (
    <Flex onBlur={handleOnBlur} className={id} vertical gap="small">
      <Checkbox
        id={`${id}-checkbox`}
        checked={onlyLastValueDate as boolean}
        onChange={handleCheckboxChange}
        disabled={!!valueDateRange}
      >
        <TextEllipsis tabIndex={-1}>{t('onlyLastValueDate')}</TextEllipsis>
      </Checkbox>
      <DatePicker.RangePicker
        id={id}
        classNames={{
          popup: { root: `${id}-popup` },
        }}
        allowEmpty={[true, true]}
        format={DATE_FORMAT}
        allowClear
        value={valueDateRange}
        onChange={handleDatePickerChange}
        // Prevent the event from bubbling up to dropdown component
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        disabled={onlyLastValueDate as boolean}
      />
    </Flex>
  );
});
